import type { ClientRoomState, RoundResult } from '@hitster/shared';
import { useCountUp } from '../hooks/useCountUp.ts';
import { useSpeaker } from '../hooks/SpeakerProvider.tsx';
import { useTickSound } from '../hooks/useTickSound.ts';
import { useT } from '../i18n.tsx';


/**
 * Die Auflösung einer Runde: Karte, Jahr und wer sie bekommt.
 *
 * Zeigt bewusst alle Teilergebnisse (Platzierung, Klau, Rate-Tipp),
 * weil sonst am Tisch nicht nachvollziehbar ist, wie die Token
 * zustande gekommen sind.
 */
export function RevealCard({
  result,
  state,
  large = false,
}: {
  result: RoundResult;
  state: ClientRoomState;
  large?: boolean;
}) {
  const t = useT();

  const nameOf = (playerId: string | null): string =>
    state.players.find((player) => player.id === playerId)?.name ?? '—';

  const { card, steal, guess } = result;

  /**
   * Die Jahreszahl läuft statt einzublenden — das ist der Moment, auf
   * den in jeder Runde alle warten.
   *
   * `settled` hält das Urteil zurück, bis die Zahl steht. Erschiene
   * „richtig" sofort, wäre der laufende Zähler wertlos: alle lesen das
   * Ergebnis und schauen der Zahl nicht mehr zu.
   */
  /**
   * Ein Klick pro Sprung.
   *
   * Der Zähler springt in ungleichen Abständen und wird zum Ende hin
   * langsamer — hörbar wirkt das auch, wenn niemand auf den Bildschirm
   * schaut. Der Ton liegt bewusst sehr leise über der Musik.
   */
  const speaker = useSpeaker();
  const { tick } = useTickSound(speaker.volume);
  const { value: shownYear, settled } = useCountUp(card.year, { onStep: tick });

  /**
   * `--cover` wird auf Seitenebene gesetzt und hierher vererbt. Die
   * Farbe stammt aus dem Albumcover, damit jede Runde anders aussieht;
   * fällt das Auslesen aus, greift die Signaturfarbe.
   */
  return (
    <div
      className={`reveal ${
        // Neutral, solange die Zahl läuft -- sonst verrät die Farbe
        // das Ergebnis, bevor die Zahl überhaupt angekommen ist.
        !settled
          ? 'reveal--pending'
          : result.activeCorrect
            ? 'reveal--correct'
            : 'reveal--wrong'
      }`}
    >
      {card.coverUrl && (
        <img
          className="reveal__cover"
          src={card.coverUrl}
          alt=""
          style={large ? { width: 220, height: 220 } : undefined}
        />
      )}

      <div className="reveal__year">{shownYear}</div>

      {/* Ist das Jahr nicht gepruefft, muss das dabeistehen. Sonst
          erscheint eine Fehlwertung durch ein Remaster-Datum als
          Willkuer, und niemand kann es nachvollziehen. */}
      {card.yearSource !== 'musicbrainz' && card.yearSource !== 'manual' && (
        <span className="text-xs" style={{ color: 'var(--warning)' }}>
          {t('reveal.yearSourceSpotify')}
        </span>
      )}

      <div>
        <div style={{ fontSize: large ? '1.75rem' : '1.125rem', fontWeight: 700 }}>
          {card.title}
        </div>
        <div className="text-muted">{card.artists.join(', ')}</div>
      </div>

      {/*
       * Alles hierin verrät das Ergebnis und bleibt unsichtbar, bis die
       * Zahl steht.
       *
       * Der Block wird trotzdem IMMER gerendert. Ein Platzhalter mit
       * geschätzter Höhe wäre falsch, sobald eine Zeile dazukommt --
       * geklaut, geraten, Karte verworfen. So ergibt sich die Höhe aus
       * dem echten Inhalt und im Moment der Auflösung springt nichts.
       *
       * `visibility: hidden` statt `opacity: 0`: nur damit ist der Text
       * auch für Screenreader und die Textsuche verborgen.
       */}
      {/*
       * `aria-live` gehört HIERHER und nicht an die Jahreszahl.
       *
       * Die Zahl wechselt ein Dutzend Mal in zwei Sekunden — als
       * Live-Region würde sie jede Vorlesestimme zumüllen. Das Urteil
       * dagegen erscheint genau einmal, und ohne Live-Region bekämen
       * blinde Mitspieler es gar nicht mit: `visibility` wechselt still.
       *
       * `polite` statt `assertive`: die Auflösung ist wichtig, aber
       * kein Grund, jemanden mitten im Satz zu unterbrechen.
       */}
      <div
        className={`reveal__result ${settled ? 'reveal__result--shown' : ''}`}
        aria-live="polite"
      >
        <div className="reveal__verdict">
          {result.activeCorrect ? t('reveal.correct') : t('reveal.wrong')}
        </div>

        <div className="stack stack--tight reveal__outcome" style={{ alignItems: 'center' }}>
          {/* Wohin die Karte geht */}
          <span>
            {result.awardedTo
              ? t('reveal.cardTo', { name: nameOf(result.awardedTo) })
              : t('reveal.discarded')}
          </span>

          {/* Klau-Ergebnis */}
          {steal && (
            <span className="text-sm text-muted">
              {steal.awarded
                ? t('reveal.stealWon', { name: nameOf(steal.playerId) })
                : steal.correct
                  ? t('reveal.stealWasted', { name: nameOf(steal.playerId) })
                  : t('reveal.stealFailed', { name: nameOf(steal.playerId) })}
            </span>
          )}

          {/* Titel/Interpret-Tipp */}
          {guess && (
            <span className="text-sm text-muted">
              {guess.titleCorrect && guess.artistCorrect
                ? t('reveal.guessBoth')
                : guess.titleCorrect
                  ? t('reveal.guessTitleOnly')
                  : guess.artistCorrect
                    ? t('reveal.guessArtistOnly')
                    : t('reveal.guessWrong')}
            </span>
          )}
        </div>

        {/* Liegt über dem verborgenen Urteil, nimmt also keinen Platz ein. */}
        {!settled && <span className="reveal__suspense" aria-hidden="true" />}
      </div>
    </div>
  );
}
